import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { usePoll, matchName, placeLine, seeded } from './ui'

/**
 * One court, on the screen mounted beside it.
 *
 * This is the whole point of the board: a display at a badminton court shows
 * that badminton court. It fills the television, carries no navigation, reads
 * from across a hall, and refreshes itself — so it can be set up in the morning
 * and left alone.
 */
const D = {
  ground: '#0b1017',
  panel: '#141c26',
  line: '#243141',
  text: '#f2f6fa',
  dim: '#8fa2b7',
  live: '#ff8534',
  win: '#4ade80',
}

const CourtScreen = () => {
  const { city, sport, court } = useParams()
  const navigate = useNavigate()

  const { data, error } = usePoll(
    () => reportingApi.court({ city, sport, court }),
    10000,
    [city, sport, court],
  )

  // Esc leaves the screen, the way a full-screen view should.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') navigate(`/board/venue/${encodeURIComponent(city)}/${sport}`)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [city, sport, navigate])

  const m = data?.now

  return (
    <div style={{
      position: 'fixed', inset: 0, background: D.ground, color: D.text,
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Where this screen is. Always on show, so nobody has to ask. */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: 'clamp(12px, 1.6vw, 26px) clamp(16px, 2.4vw, 40px)',
        borderBottom: `1px solid ${D.line}`, gap: 16, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'clamp(10px,1.4vw,22px)', minWidth: 0 }}>
          <span style={{
            fontSize: 'clamp(20px, 2.6vw, 42px)', fontWeight: 800, letterSpacing: '-.01em',
          }}>{decodeURIComponent(court)}</span>
          <span style={{
            fontSize: 'clamp(12px, 1.15vw, 20px)', color: D.dim, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {placeLine({ label: data?.label, venue: data?.venue }, decodeURIComponent(city))}
          </span>
        </div>

        <Link to={`/board/venue/${encodeURIComponent(city)}/${sport}`} style={{
          color: D.dim, textDecoration: 'none', fontSize: 'clamp(11px,.9vw,15px)',
          border: `1px solid ${D.line}`, borderRadius: 6, padding: '5px 11px',
          fontWeight: 700, flexShrink: 0,
        }}>Esc</Link>
      </header>

      <main style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'clamp(16px, 2.4vw, 48px)', minHeight: 0,
      }}>
        {error && <Centered dim>{error}</Centered>}
        {!data && !error && <Centered dim>Connecting…</Centered>}

        {data && !m && <Idle data={data} />}
        {m && <LiveMatch m={m} overs={data.over_by_over} />}
      </main>

      {data?.next?.length > (m ? 0 : 1) && (
        <footer style={{
          borderTop: `1px solid ${D.line}`, background: D.panel,
          padding: 'clamp(10px,1.2vw,20px) clamp(16px,2.4vw,40px)', flexShrink: 0,
        }}>
          <div style={{
            fontSize: 'clamp(10px,.75vw,13px)', letterSpacing: '.14em', textTransform: 'uppercase',
            color: D.dim, fontWeight: 800, marginBottom: 8,
          }}>Next on this {(data.unit_noun || 'court').toLowerCase()}</div>

          <div style={{ display: 'flex', gap: 'clamp(16px,2.4vw,44px)', overflow: 'hidden' }}>
            {data.next.slice(m ? 0 : 1, m ? 3 : 4).map((n) => (
              <div key={n.id} style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 'clamp(13px,1.1vw,20px)', fontWeight: 800, color: D.live,
                  fontVariantNumeric: 'tabular-nums',
                }}>{n.start_time || '—'} <span style={{ color: D.dim, fontWeight: 600 }}>{n.day}</span></div>
                <div style={{
                  fontSize: 'clamp(12px,1vw,18px)', color: D.text, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{matchName(n)}</div>
              </div>
            ))}
          </div>
        </footer>
      )}
    </div>
  )
}

/** The match in progress: names big, score bigger. */
const LiveMatch = ({ m, overs }) => (
  <>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, marginBottom: 'clamp(10px,1.6vw,28px)',
      justifyContent: 'center',
    }}>
      <span style={{
        width: 'clamp(8px,.7vw,13px)', height: 'clamp(8px,.7vw,13px)', borderRadius: '50%',
        background: D.live, animation: 'none',
      }} />
      <span style={{
        color: D.live, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase',
        fontSize: 'clamp(11px,.95vw,17px)',
      }}>Live</span>
      <span style={{ color: D.dim, fontSize: 'clamp(11px,.95vw,17px)', fontWeight: 600 }}>
        {[m.category, m.round].filter(Boolean).join(' · ')}
      </span>
    </div>

    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto 1fr',
      gap: 'clamp(12px,2vw,44px)', alignItems: 'center',
    }}>
      <Competitor name={m.home} seed={m.home_seed} align="right" />

      <div style={{ textAlign: 'center' }}>
        {m.summary ? (
          <div style={{
            fontSize: 'clamp(38px, 7.5vw, 132px)', fontWeight: 800, lineHeight: 1,
            color: D.live, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em',
          }}>{m.summary}</div>
        ) : (
          <div style={{ fontSize: 'clamp(20px,3vw,52px)', color: D.dim, fontWeight: 700 }}>v</div>
        )}

        {m.note && (
          <div style={{
            marginTop: 'clamp(6px,.9vw,16px)', color: D.dim, fontWeight: 600,
            fontSize: 'clamp(12px,1.15vw,22px)',
          }}>{m.note}</div>
        )}

        {/* Games already banked, for the racket sports. */}
        {Array.isArray(m.sets) && m.sets.length > 0 && (
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'center', marginTop: 'clamp(8px,1.1vw,18px)',
          }}>
            {m.sets.map((s, i) => (
              <div key={i} style={{
                border: `1px solid ${D.line}`, borderRadius: 6, padding: '4px 10px',
                fontSize: 'clamp(12px,1.1vw,20px)', fontWeight: 700,
                fontVariantNumeric: 'tabular-nums', color: D.text,
              }}>{s.home}–{s.away}</div>
            ))}
          </div>
        )}
      </div>

      <Competitor name={m.away} seed={m.away_seed} align="left" />
    </div>

    {/* Cricket earns an extra line: how the last over went. */}
    {overs?.length > 0 && (
      <div style={{
        marginTop: 'clamp(16px,2.4vw,44px)', display: 'flex', gap: 'clamp(12px,1.6vw,28px)',
        justifyContent: 'center', flexWrap: 'wrap',
      }}>
        {overs.slice(-2).map((o) => (
          <div key={o.over} style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 'clamp(10px,.8vw,14px)', color: D.dim, fontWeight: 800,
              letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6,
            }}>Over {o.over} · {o.runs}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {o.balls.map((b, i) => (
                <span key={i} style={{
                  minWidth: 'clamp(24px,2.2vw,42px)', padding: '4px 0', borderRadius: '50%',
                  background: b === 'W' ? '#7f1d1d' : D.panel,
                  border: `1px solid ${b === 'W' ? '#b91c1c' : D.line}`,
                  fontSize: 'clamp(12px,1.05vw,19px)', fontWeight: 800,
                  color: b === '4' || b === '6' ? D.live : D.text,
                }}>{b}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </>
)

/** Nothing on court: show what is coming, and what just went. */
const Idle = ({ data }) => {
  const next = data.next?.[0]
  const last = data.last?.[0]

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        color: D.dim, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 800,
        fontSize: 'clamp(11px,.95vw,17px)',
      }}>No match on this {(data.unit_noun || 'court').toLowerCase()}</div>

      {next && (
        <div style={{ marginTop: 'clamp(14px,2vw,36px)' }}>
          <div style={{
            fontSize: 'clamp(30px,5.5vw,96px)', fontWeight: 800, color: D.live,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
          }}>{next.start_time}</div>
          <div style={{
            fontSize: 'clamp(14px,1.5vw,28px)', color: D.dim, marginTop: 6, fontWeight: 600,
          }}>{next.day}</div>
          <div style={{
            fontSize: 'clamp(18px,2.4vw,44px)', fontWeight: 700, marginTop: 'clamp(8px,1.2vw,20px)',
          }}>{matchName(next)}</div>
          <div style={{ fontSize: 'clamp(12px,1.1vw,20px)', color: D.dim, marginTop: 6 }}>
            {[next.category, next.round].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}

      {!next && <div style={{
        marginTop: 24, fontSize: 'clamp(16px,2vw,36px)', color: D.dim,
      }}>Nothing further scheduled here.</div>}

      {last && (
        <div style={{
          marginTop: 'clamp(20px,3vw,56px)', paddingTop: 'clamp(14px,2vw,30px)',
          borderTop: `1px solid ${D.line}`, display: 'inline-block',
        }}>
          <div style={{
            fontSize: 'clamp(10px,.8vw,14px)', color: D.dim, letterSpacing: '.14em',
            textTransform: 'uppercase', fontWeight: 800,
          }}>Last result here</div>
          <div style={{ fontSize: 'clamp(14px,1.5vw,28px)', marginTop: 8, fontWeight: 700 }}>
            {seeded(last.home, last.home_seed)}{' '}
            <span style={{ color: D.live }}>{last.summary || ''}</span>{' '}
            {seeded(last.away, last.away_seed)}
          </div>
          {last.winner && (
            <div style={{ color: D.win, fontSize: 'clamp(12px,1.1vw,20px)', marginTop: 4, fontWeight: 600 }}>
              {last.winner} went through
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const Competitor = ({ name, seed, align }) => (
  <div style={{
    textAlign: align === 'right' ? 'right' : 'left',
    fontSize: 'clamp(18px, 2.9vw, 56px)', fontWeight: 700, lineHeight: 1.15,
    textWrap: 'balance', minWidth: 0, wordBreak: 'break-word',
  }}>
    {name}
    {seed && (
      <span style={{
        color: D.dim, fontWeight: 600, fontSize: '.5em', marginLeft: '.35em',
        whiteSpace: 'nowrap',
      }}>[{seed}]</span>
    )}
  </div>
)

const Centered = ({ children, dim }) => (
  <div style={{
    textAlign: 'center', color: dim ? D.dim : D.text, fontSize: 'clamp(14px,1.6vw,28px)',
  }}>{children}</div>
)

export default CourtScreen
